/**
 * 파일명 : evl0102m.tsx
 * 설명 : 업무서약서 화면 - 모바일
 * 수정내역
 ************************************************
 * Version    수정일자      수정자    수정내용
 ************************************************
 * 1.0       2026-01-08     변재석    최초작성
 * 1.1       2026-01-26     조동현    모바일 버전 생성
 ***********************************************/

/************************************************
 * Import 영역
 ************************************************/
/*  React  */
import React, { useState, useEffect, useRef } from "react";

/*  AntD  */
import {
  Input,
  Button,
  Space,
  Typography,
  DatePicker,
  message,
  Popconfirm,
  Modal,
  Select,
  Card,
  Spin,
} from "antd";
import {
  PlusOutlined,
  SaveOutlined,
  SendOutlined,
  CheckCircleFilled,
} from "@ant-design/icons";
import { isMobile } from "react-device-detect";
import dayjs from "dayjs";

/*  업무  */
import * as common from "libs/common";
import * as commonProxy from "libs/CommonProxy";

/* Reducer - 타입 정의 */
import type {
  ITaskRow,
  IEtcRow,
  IUserInfo,
  IPropsEvl0102M,
} from "../../../redux/models/evl/evl0102m_model";

/* Reducer - 상수 및 유틸 함수 */
import {
  DTL_TYP_CD,
  createDefaultUserInfo,
  createDefaultTaskRow,
  createDefaultTeamContribution,
  createDefaultSelfDevelopment,
  parsePeriodFromDates,
  calculateTotalWeight,
  parseTeamContributionFromResponse,
  parseSelfDevelopmentFromResponse,
} from "../../../redux/slice/evl/evl0102m_slice";

const { TextArea } = Input;
const { Text } = Typography;

// 타입 별칭 (기존 코드 호환성을 위해)
type TaskRow = ITaskRow;
type EtcRow = IEtcRow;
type UserInfo = IUserInfo;
type Evl0102mProps = IPropsEvl0102M;

/**
 * 컴포넌트명 : Evl0102m
 * 기능 : 업무서약서 작성 화면 메인 컴포넌트
 * 설명 : 본인 정보, 담당 직무(동적 추가/삭제), 기타 서약, 자기계발 부문으로 구성
 *        담당 직무의 비중 합계가 100%일 때만 제출 가능
 **/
const Evl0102m: React.FC<Evl0102mProps> = (props) => {
  // 평가년도 (props에서 받거나 현재 연도)
  const currentYear = new Date().getFullYear().toString();

  // props에서 값 추출 (props 우선, 없으면 세션/기본값 사용)
  const empNo = props.empNo || common.getEmpNo();
  const evlYear = props.evlYear || currentYear;
  const evlEnableYn = props.evlEnableYn || "Y";

  // 읽기 전용 모드 (evl0103m 최종평가결과에서 조회용 / 메인페이지에서 본인 이외의 업무서약서 조회 시)
  const isReadonly =
    props.viewMode === "readonly" || props.empNo !== common.getEmpNo();

  // 제출 여부 (DB에서 조회)
  const [bizPlgSubYn, setBizPlgSubYn] = useState("N");

  // 서버 기반 마감일 정보
  const [bizPlgModEdDt, setBizPlgModEdDt] = useState<string>("");
  const [isWithinDeadlineFromServer, setIsWithinDeadlineFromServer] =
    useState<boolean>(false);

  // 로딩 상태
  const [loading, setLoading] = useState<boolean>(true);

  // Props 검증 상태 (props.empNo, props.evlYear가 존재하는지 추적)
  const [isPropsValid, setIsPropsValid] = useState(false);

  // 검증 실패 시 스크롤 이동용 ref들
  const grdSelectRef = useRef<any>(null);
  const currentGradeStartDateRef = useRef<HTMLDivElement>(null);
  const tasksContainerRef = useRef<HTMLDivElement>(null);

  const [screenWidth, setScreenWidth] = useState<number>(window.innerWidth);
  React.useEffect(() => {
    const handleResize = () => setScreenWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // 수정 가능 여부 (evlEnableYn === 'Y' 이고 bizPlgSubYn !== 'Y' 일 때만 저장/제출 가능)
  const isEditable = evlEnableYn === "Y" && bizPlgSubYn !== "Y";

  // 마감일 이내 여부: 서버 계산값 기준
  const isWithinDeadline = isWithinDeadlineFromServer;

  // 제출취소 가능 여부
  const isCancelable = bizPlgSubYn === "Y" && isWithinDeadline;

  // 직군코드 (064 코드그룹)
  const [grdCd, setGrdCd] = useState("");
  const [grdCdOptions, setGrdCdOptions] = useState<
    { cd: string; cdNm: string }[]
  >([]);

  /**
   * 기능 : userInfo state
   * 설명 : DB에서 받아온 본인 정보를 관리하는 상태 (읽기 전용)
   **/
  const [userInfo, setUserInfo] = useState<UserInfo>(createDefaultUserInfo());

  /**
   * 기능 : pledgePeriod state
   * 설명 : 업무서약기간 (평가년도 기준 자동 세팅)
   **/
  const [pledgePeriod, setPledgePeriod] = useState<[any, any]>([
    dayjs(`${evlYear}-01-01`),
    dayjs(`${evlYear}-12-31`),
  ]);

  /**
   * 기능 : currentGradeStartDate state
   * 설명 : 현직급시작일자
   **/
  const [currentGradeStartDate, setCurrentGradeStartDate] = useState<any>(null);

  /**
   * 기능 : taskHrEvlItmId state
   * 설명 : 담당직무 항목의 hrEvlItmId (TEVL003에서 조회)
   *        새 업무 추가 시 이 값을 사용
   **/
  const [taskHrEvlItmId, setTaskHrEvlItmId] = useState<string>("");

  /**
   * 기능 : tasks state
   * 설명 : 담당 직무 목록을 관리하는 상태
   *        각 task는 hrEvlItmId, sno, name(업무명), goal(업무목표), weight(비중), period(기간) 속성을 가짐
   **/
  const [tasks, setTasks] = useState<TaskRow[]>([
    createDefaultTaskRow(1, evlYear),
  ]);

  /**
   * 기능 : nextId state
   * 설명 : 새로운 업무를 추가할 때 사용할 고유 ID 값을 관리
   **/
  const [nextId, setNextId] = useState(2);

  /**
   * 기능 : teamContribution state
   * 설명 : 팀기여 부문 데이터 관리
   **/
  const [teamContribution, setTeamContribution] = useState<EtcRow[]>(
    createDefaultTeamContribution(evlYear),
  );

  /**
   * 기능 : selfDevelopment state
   * 설명 : 자기개발 부문 데이터 관리
   **/
  const [selfDevelopment, setSelfDevelopment] = useState<EtcRow[]>(
    createDefaultSelfDevelopment(evlYear),
  );

  /**
   * 기능 : messageApi
   * 설명 : Ant Design의 message 컴포넌트를 사용하여 알림 메시지를 표시하기 위한 API
   **/
  const [messageApi, contextHolder] = message.useMessage();
  const confirmModalRefs = useRef<any[]>([]);

  const removeConfirmModalRef = (targetModal: any) => {
    if (!targetModal) return;
    confirmModalRefs.current = confirmModalRefs.current.filter(
      (modalRef) => modalRef !== targetModal,
    );
  };

  const closeOpenConfirmModals = () => {
    confirmModalRefs.current.forEach((modalRef) => {
      try {
        modalRef?.destroy?.();
      } catch (error) {
        // ignore
      }
    });
    confirmModalRefs.current = [];
  };

  const openConfirmModal = (config: any) => {
    let createdModal: any;
    createdModal = Modal.confirm({
      ...config,
      onOk: (...args: any[]) => {
        const result = config?.onOk?.(...args);
        removeConfirmModalRef(createdModal);
        return result;
      },
      onCancel: (...args: any[]) => {
        const result = config?.onCancel?.(...args);
        removeConfirmModalRef(createdModal);
        return result;
      },
    });

    confirmModalRefs.current.push(createdModal);
    return createdModal;
  };

  useEffect(() => {
    const handlePopState = () => {
      closeOpenConfirmModals();
    };

    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
      closeOpenConfirmModals();
    };
  }, []);

  /**
   * 기능 : useEffect - 컴포넌트 마운트 시 필수 값 체크 및 직군명 조회
   * 설명 : empNo, evlYear가 없으면 경고 메시지 표시하고 뒤로가기, 코드그룹 '064'에서 직군명 조회
   **/
  useEffect(() => {
    // props.empNo와 props.evlYear 체크
    if (!props.empNo || !props.evlYear) {
      setIsPropsValid(false);
      // loading을 true로 유지하여 background에 로딩 표시
      setLoading(true);
      openConfirmModal({
        title: "인사평가 메인페이지에서 접근하십시오.",
        okText: "확인",
        cancelButtonProps: { style: { display: "none" } }, // 취소 버튼 숨김
        maskClosable: false, // 바깥 클릭 방지
        keyboard: false, // ESC 방지
        onOk: () => {
          // 인사평가 메인 페이지로 이동
          common.openPage({ menuId: "MEVL2025002" });
        },
        onCancel() {
          // 인사평가 메인 페이지로 이동
          common.openPage({ menuId: "MEVL2025002" });
        },
      });
      return; // 이후 코드 실행 안 함
    }

    setIsPropsValid(true);

    const option = {
      useYn: "",
      callback: (codeItems: any[]) => {
        if (codeItems && Array.isArray(codeItems)) {
          setGrdCdOptions(
            codeItems.map((item: any) => ({ cd: item.cd, cdNm: item.cdNm })),
          );
        }
      },
    };
    common.getComCodeList("064", option);
  }, [props.empNo, props.evlYear]);

  /**
   * 기능 : loadPledgeData
   * 설명 : 업무서약서 조회 API 호출
   **/
  const loadPledgeData = () => {
    const requestData = {
      empNo: empNo, // props에서 받은 값 사용
      evlYear: evlYear,
    };

    commonProxy.callAsyncSvc(requestData, {
      txCd: "TREVLR0012", // 업무서약서 조회
      success: (responseData: any) => {
        const data = responseData.data || responseData;
        const header = data.header || {};
        const list = data.list || [];

        // 1. 본인 정보 설정 (grdNm은 마운트 시 064로 조회됨)
        setUserInfo((prev) => ({
          ...prev,
          userNm: header.userNm || "",
          postNm: header.postNm || "",
          dutyNm: header.dutyNm || "",
          upperDeptNm: header.upperDeptNm || "",
          deptNm: header.deptNm || "",
        }));

        // 2. TEVL010에서 받아온 본인정보 데이터 처리
        // 제출 여부
        setBizPlgSubYn(header.bizPlgSubYn || "N");

        // 서버 기반 마감일 정보
        setBizPlgModEdDt(data.bizPlgModEdDt || "");
        setIsWithinDeadlineFromServer(data.isWithinDeadline === true);

        // 직군코드 (DB에서 받아온 값이 있으면 사용, 없으면 유지)
        if (header.grdCd) {
          setGrdCd(header.grdCd);
          // grdNm도 업데이트
          const grdMap = new Map(grdCdOptions.map((opt) => [opt.cd, opt.cdNm]));
          setUserInfo((prev) => ({
            ...prev,
            grdNm: grdMap.get(header.grdCd) || prev.grdNm,
          }));
        } else {
          // 값이 없으면 빈 상태 유지
          setGrdCd("");
          setUserInfo((prev) => ({
            ...prev,
            grdNm: "",
          }));
        }

        // 현직급시작일자 (YYYYMMDD 형식)
        if (header.currPosStDt) {
          const currDate = String(header.currPosStDt).trim();
          if (/^\d{8}$/.test(currDate)) {
            setCurrentGradeStartDate(dayjs(currDate, "YYYYMMDD"));
          }
        }

        // 업무서약기간 (bizPlgStDt, bizPlgEdDt)
        if (header.bizPlgStDt && header.bizPlgEdDt) {
          const stDt = String(header.bizPlgStDt).trim();
          const edDt = String(header.bizPlgEdDt).trim();
          if (/^\d{8}$/.test(stDt) && /^\d{8}$/.test(edDt)) {
            setPledgePeriod([dayjs(stDt, "YYYYMMDD"), dayjs(edDt, "YYYYMMDD")]);
          }
        } else {
          // 값이 없으면 평가년도 기본값 세팅
          setPledgePeriod([
            dayjs(`${evlYear}-01-01`),
            dayjs(`${evlYear}-12-31`),
          ]);
        }

        // 3. DTL_TYP_CD별로 데이터 분류 (동적으로 처리)
        const taskList: TaskRow[] = [];
        let taskId = 1;

        // 담당직무 항목의 hrEvlItmId 찾기 (TEVL003에서 조회된 값)
        const taskItem = list.find(
          (item: any) => item.dtlTypCd === DTL_TYP_CD.TASK,
        );
        const loadedTaskHrEvlItmId = taskItem?.hrEvlItmId || "";

        list.forEach((item: any) => {
          if (item.dtlTypCd === DTL_TYP_CD.TASK && item.sno) {
            // 담당 직무
            taskList.push({
              id: taskId++,
              hrEvlItmId: item.hrEvlItmId,
              sno: item.sno,
              name: item.taskNm || "",
              goal: item.goalCntn || "",
              weight: item.weightRt ? item.weightRt.toString() : "",
              period:
                parsePeriodFromDates(item.bizPlgStDt, item.bizPlgEdDt) ||
                defaultPeriod,
            });
          }
        });

        // 팀기여/자기개발 동적 파싱
        const teamContribList = parseTeamContributionFromResponse(
          list,
          evlYear,
        );
        const selfDevList = parseSelfDevelopmentFromResponse(list, evlYear);

        setTaskHrEvlItmId(loadedTaskHrEvlItmId);

        // 기본 기간 (평가년도 기준)
        const defaultPeriod: [any, any] = [
          dayjs(`${evlYear}-01-01`),
          dayjs(`${evlYear}-12-31`),
        ];

        // 담당 직무가 없으면 빈 항목 1개 추가 (평가년도 기본 기간 세팅)
        if (taskList.length === 0) {
          taskList.push({
            id: 1,
            hrEvlItmId: loadedTaskHrEvlItmId,
            name: "",
            goal: "",
            weight: "",
            period: defaultPeriod,
          });
        }

        const normalizedTaskList = taskList.map((item) => ({
          ...item,
          period: item.period || defaultPeriod,
        }));

        // 담당직무는 조회된 전체 목록을 표시
        setTasks(normalizedTaskList);
        setNextId(Math.max(normalizedTaskList.length, 1) + 1);
        setTeamContribution(teamContribList);
        setSelfDevelopment(selfDevList);

        messageApi.success("업무서약서를 조회했습니다.");
        setLoading(false);
      },
      error: (errorMsg: string) => {
        messageApi.error(errorMsg || "업무서약서 조회에 실패했습니다.");
        setLoading(false);
      },
    });
  };

  /**
   * 기능 : useEffect
   * 설명 : empNo, evlYear가 변경될 때 조회 API 호출
   *        props.empNo와 props.evlYear가 없으면 조회 API 호출하지 않음
   **/
  useEffect(() => {
    // props 검증이 실패한 경우 조회 API 호출하지 않음
    if (!isPropsValid) {
      return;
    }

    loadPledgeData();
  }, [isPropsValid, empNo, evlYear]);

  /**
   * 기능 : updateTask
   * 설명 : 특정 업무의 특정 필드 값을 업데이트
   * param :
   *   - id: 업데이트할 업무의 고유 ID
   *   - field: 업데이트할 필드명 (name, goal, weight 등)
   *   - value: 새로운 값
   * return : None
   **/
  const updateTask = (id: number, field: keyof TaskRow, value: string) => {
    setTasks((prev) => {
      const updated = prev.map((t) => {
        if (t.id === id && t[field] !== value) {
          return { ...t, [field]: value };
        }
        return t;
      });
      // 기존 배열과 동일하면 업데이트하지 않음
      return updated.some((t, idx) => t !== prev[idx]) ? updated : prev;
    });
  };

  /**
   * 기능 : addTask
   * 설명 : 새로운 빈 업무를 목록에 추가
   * param : None
   * return : None
   **/
  const addTask = () => {
    const newTask = createDefaultTaskRow(nextId, evlYear);
    newTask.hrEvlItmId = taskHrEvlItmId;
    setTasks([...tasks, newTask]);
    setNextId(nextId + 1);
  };

  /**
   * 기능 : removeTask
   * 설명 : 특정 업무를 목록에서 삭제 (최소 1개는 유지)
   * param : id - 삭제할 업무의 고유 ID
   * return : None
   **/
  const removeTask = (id: number) => {
    if (tasks.length > 1) {
      setTasks(tasks.filter((t) => t.id !== id));
    }
  };

  /**
   * 기능 : getTotalWeight
   * 설명 : 모든 업무의 비중 합계를 계산
   * param : None
   * return : 비중 합계 (숫자)
   **/
  const getTotalWeight = () => calculateTotalWeight(tasks);

  /**
   * 기능 : handleSubmit
   * 설명 : 제출 버튼 클릭 시 실행되는 함수
   *        모든 필수 입력값 검증 및 비중 합계 100% 확인 후 제출
   * param : None
   * return : None
   **/
  const handleSubmit = () => {
    // 스크롤 이동 헬퍼 함수
    const scrollAndError = (
      ref: React.RefObject<HTMLElement>,
      errorMsg: string,
    ) => {
      messageApi.error(errorMsg);
      setTimeout(() => {
        if (ref.current) {
          ref.current.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
          ref.current.focus?.();
        }
      }, 300);
    };

    // task index로 스크롤하는 헬퍼 함수
    const scrollAndErrorByTaskIndex = (taskIndex: number, errorMsg: string) => {
      messageApi.error(errorMsg);
      setTimeout(() => {
        const element = document.querySelector(
          `[data-task-index="${taskIndex}"]`,
        );
        if (element) {
          element.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }
      }, 300);
    };

    // 팀기여 index로 스크롤하는 헬퍼 함수
    const scrollAndErrorByTeamIndex = (teamIndex: number, errorMsg: string) => {
      messageApi.error(errorMsg);
      setTimeout(() => {
        const element = document.querySelector(
          `[data-team-index="${teamIndex}"]`,
        );
        if (element) {
          element.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }
      }, 300);
    };

    // 자기개발 index로 스크롤하는 헬퍼 함수
    const scrollAndErrorBySelfIndex = (selfIndex: number, errorMsg: string) => {
      messageApi.error(errorMsg);
      setTimeout(() => {
        const element = document.querySelector(
          `[data-self-index="${selfIndex}"]`,
        );
        if (element) {
          element.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }
      }, 300);
    };

    // 1. 직군 선택 검증
    if (!grdCd) {
      scrollAndError(grdSelectRef, "직군을 선택해주세요.");
      return;
    }

    // 2. 현직급시작일자 검증
    if (!currentGradeStartDate) {
      scrollAndError(
        currentGradeStartDateRef,
        "현직급시작일자를 입력해주세요.",
      );
      return;
    }

    // 3. 업무서약기간 검증
    if (!pledgePeriod || !pledgePeriod[0] || !pledgePeriod[1]) {
      messageApi.error("업무서약기간을 입력해주세요.");
      return;
    }

    // 3-1. 업무서약기간 날짜 유효성 검증
    if (dayjs(pledgePeriod[0]).isAfter(dayjs(pledgePeriod[1]))) {
      messageApi.warning("업무서약기간 - 시작일이 종료일보다 클 수 없습니다.");
      return;
    }

    // 4. 담당 직무 검증
    for (let i = 0; i < tasks.length; i++) {
      const task = tasks[i];

      if (!task.name.trim()) {
        scrollAndErrorByTaskIndex(i, `업무 ${i + 1}의 업무명을 입력해주세요.`);
        return;
      }

      if (!task.weight || parseFloat(task.weight) <= 0) {
        scrollAndErrorByTaskIndex(i, `업무 ${i + 1}의 비중을 입력해주세요.`);
        return;
      }

      if (!task.period || !task.period[0] || !task.period[1]) {
        scrollAndErrorByTaskIndex(i, `업무 ${i + 1}의 기간을 입력해주세요.`);
        return;
      }

      // 담당 직무 기간 날짜 유효성 검증
      if (dayjs(task.period[0]).isAfter(dayjs(task.period[1]))) {
        messageApi.warning(
          `업무 ${i + 1} - 시작일이 종료일보다 클 수 없습니다.`,
        );
        return;
      }

      if (!task.goal.trim()) {
        scrollAndErrorByTaskIndex(
          i,
          `업무 ${i + 1}의 업무 목표를 입력해주세요.`,
        );
        return;
      }

      if (task.goal.length > 500) {
        scrollAndErrorByTaskIndex(
          i,
          `업무 ${i + 1}의 업무 목표는 500자 이하로 입력해주세요. (현재 ${task.goal.length}자)`,
        );
        return;
      }
    }

    // 5. 비중 합계 검증
    if (getTotalWeight() !== 100) {
      scrollAndErrorByTaskIndex(
        0,
        "업무 비중의 합계는 100%여야 제출할 수 있습니다.",
      );
      return;
    }

    // 6. 팀기여 부문 검증
    for (let ti = 0; ti < teamContribution.length; ti++) {
      const item = teamContribution[ti];
      if (!item.content.trim()) {
        scrollAndErrorByTeamIndex(
          ti,
          `팀기여 부문 ${item.category}의 내용을 입력해주세요.`,
        );
        return;
      }
      if (item.content.length > 500) {
        scrollAndErrorByTeamIndex(
          ti,
          `팀기여 부문 ${item.category}의 내용은 500자 이하로 입력해주세요. (현재 ${item.content.length}자)`,
        );
        return;
      }
      if (!item.period || !item.period[0] || !item.period[1]) {
        scrollAndErrorByTeamIndex(
          ti,
          `팀기여 부문 ${item.category}의 기간을 입력해주세요.`,
        );
        return;
      }
      // 팀기여 부문 기간 날짜 유효성 검증
      if (dayjs(item.period[0]).isAfter(dayjs(item.period[1]))) {
        messageApi.warning(
          `팀기여 부문 ${item.category} - 시작일이 종료일보다 클 수 없습니다.`,
        );
        return;
      }
    }

    // 7. 자기개발 부문 검증
    for (let si = 0; si < selfDevelopment.length; si++) {
      const item = selfDevelopment[si];
      if (!item.content.trim()) {
        scrollAndErrorBySelfIndex(
          si,
          `자기개발 부문 ${item.category}의 내용을 입력해주세요.`,
        );
        return;
      }
      if (item.content.length > 500) {
        scrollAndErrorBySelfIndex(
          si,
          `자기개발 부문 ${item.category}의 내용은 500자 이하로 입력해주세요. (현재 ${item.content.length}자)`,
        );
        return;
      }
      if (!item.period || !item.period[0] || !item.period[1]) {
        scrollAndErrorBySelfIndex(
          si,
          `자기개발 부문 ${item.category}의 기간을 입력해주세요.`,
        );
        return;
      }
      // 자기개발 부문 기간 날짜 유효성 검증
      if (dayjs(item.period[0]).isAfter(dayjs(item.period[1]))) {
        messageApi.warning(
          `자기개발 부문 ${item.category} - 시작일이 종료일보다 클 수 없습니다.`,
        );
        return;
      }
    }

    // 제출 확인 Modal
    openConfirmModal({
      title: "제출하시겠습니까?",
      okText: "제출",
      cancelText: "취소",
      onOk: () => {
        performSubmit();
      },
    });
  };

  /**
   * 기능 : performSubmit
   * 설명 : 실제 제출 로직 실행 (submitYn='Y'로 저장)
   **/
  const performSubmit = () => {
    // 날짜 형식 변환 함수 (dayjs -> YYYYMMDD)
    const formatDate = (date: any): string => {
      if (!date) return "";
      return dayjs(date).format("YYYYMMDD");
    };

    // 날짜 범위 분해 ([dayjs, dayjs] -> [YYYYMMDD, YYYYMMDD])
    const splitPeriod = (period: [any, any] | null): [string, string] => {
      if (!period || !period[0] || !period[1]) return ["", ""];
      return [
        dayjs(period[0]).format("YYYYMMDD"),
        dayjs(period[1]).format("YYYYMMDD"),
      ];
    };

    // SNO를 2자리 문자열로 변환 (01, 02, 03...)
    const formatSno = (num: number): string => {
      return num.toString().padStart(2, "0");
    };

    // DTO 구조에 맞게 list 생성
    const list: any[] = [];

    // 담당 직무 추가 (sno: 01, 02, 03...)
    tasks.forEach((task, index) => {
      const [stDt, edDt] = splitPeriod(task.period);
      list.push({
        hrEvlItmId: task.hrEvlItmId || taskHrEvlItmId || "",
        sno: formatSno(index + 1),
        taskNm: task.name,
        goalCntn: task.goal,
        weightRt: task.weight ? parseInt(task.weight) : 0,
        bizPlgStDt: stDt,
        bizPlgEdDt: edDt,
      });
    });

    // 팀기여 부문 추가 (동적 hrEvlItmId 사용, SNO는 itmSeq 우선)
    teamContribution.forEach((item) => {
      const [stDt, edDt] = splitPeriod(item.period);
      const sno = item.itmSeq
        ? String(item.itmSeq).padStart(2, "0")
        : item.sno || "01";
      list.push({
        hrEvlItmId: item.hrEvlItmId || item.key || "",
        sno: sno,
        taskNm: item.category,
        goalCntn: item.content,
        weightRt: 0,
        bizPlgStDt: stDt,
        bizPlgEdDt: edDt,
      });
    });

    // 자기개발 부문 추가 (동적 hrEvlItmId 사용, SNO는 itmSeq 우선)
    selfDevelopment.forEach((item) => {
      const [stDt, edDt] = splitPeriod(item.period);
      const sno = item.itmSeq
        ? String(item.itmSeq).padStart(2, "0")
        : item.sno || "01";
      list.push({
        hrEvlItmId: item.hrEvlItmId || item.key || "",
        sno: sno,
        taskNm: item.category,
        goalCntn: item.content,
        weightRt: 0,
        bizPlgStDt: stDt,
        bizPlgEdDt: edDt,
      });
    });

    // 요청 데이터 생성 (제출 시 bizPlgSubYn='Y')
    const requestData = {
      empNo: empNo, // props에서 받은 값 사용
      evlYear: evlYear,
      // === TEVL010 (본인정보) - root 레벨 ===
      grdCd: grdCd,
      currPosStDt: formatDate(currentGradeStartDate),
      bizPlgStDt: splitPeriod(pledgePeriod)[0],
      bizPlgEdDt: splitPeriod(pledgePeriod)[1],
      bizPlgSubYn: "Y",
      // === TEVL009 (상세) ===
      list: list,
    };

    // API 호출
    commonProxy.callAsyncSvc(requestData, {
      txCd: "TREVLS0015", // 업무서약서 저장 (저장과 제출 공통 사용)
      success: (responseData: any) => {
        const data = responseData.data || responseData;

        if (data.errorMessage) {
          openConfirmModal({
            title: "제출 실패",
            content: data.errorMessage,
            okText: "확인",
            cancelButtonProps: { style: { display: "none" } },
            onOk: () => {
              common.openPage({ menuId: "MEVL2025002" });
            },
          });
        } else {
          messageApi.success("제출되었습니다.");
          setTimeout(() => {
            common.openPage({ menuId: "MEVL2025002" });
          }, 800);
        }
      },
      error: (errorMsg: string) => {
        openConfirmModal({
          title: "오류",
          content: errorMsg || "제출에 실패했습니다.",
          okText: "확인",
          cancelButtonProps: { style: { display: "none" } },
          onOk: () => {
            common.openPage({ menuId: "MEVL2025002" });
          },
        });
      },
    });
  };

  /**
   * 기능 : handleCancelSubmit
   * 설명 : 제출취소 버튼 클릭 시 실행되는 함수
   **/
  const handleCancelSubmit = () => {
    openConfirmModal({
      title: "제출을 취소하시겠습니까?",
      content: "제출취소 후 기한 내에 다시 제출할 수 있습니다.",
      okText: "확인",
      cancelText: "취소",
      onOk: () => {
        performCancelSubmit();
      },
    });
  };

  /**
   * 기능 : performCancelSubmit
   * 설명 : 실제 제출취소 로직 실행 (bizPlgSubYn='N'으로 저장)
   **/
  const performCancelSubmit = () => {
    const requestData = {
      empNo: empNo,
      evlYear: evlYear,
      bizPlgSubYn: "N",
    };

    commonProxy.callAsyncSvc(requestData, {
      txCd: "TREVLS0015", // 업무서약서 저장 (저장/제출/취소 공통)
      success: (responseData: any) => {
        const data = responseData.data || responseData;

        if (data.errorMessage) {
          openConfirmModal({
            title: "제출취소 실패",
            content: data.errorMessage,
            okText: "확인",
            cancelButtonProps: { style: { display: "none" } },
            onOk: () => {
              common.openPage({ menuId: "MEVL2025002" });
            },
          });
        } else {
          messageApi.success("제출취소 되었습니다.");
          setTimeout(() => {
            common.openPage({ menuId: "MEVL2025002" });
          }, 800);
        }
      },
      error: (errorMsg: string) => {
        openConfirmModal({
          title: "오류",
          content: errorMsg || "제출취소에 실패했습니다.",
          okText: "확인",
          cancelButtonProps: { style: { display: "none" } },
          onOk: () => {
            common.openPage({ menuId: "MEVL2025002" });
          },
        });
      },
    });
  };

  /**
   * 기능 : handleSave
   * 설명 : 저장 버튼 클릭 시 실행되는 함수
   * param : None
   * return : None
   **/
  const handleSave = () => {
    // 0. 담당 직무 비중 합계 검증
    if (getTotalWeight() !== 100) {
      messageApi.error("업무 비중의 합계는 100%여야 저장할 수 있습니다.");
      setTimeout(() => {
        const element = document.querySelector('[data-task-index="0"]');
        if (element) {
          element.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }
      }, 300);
      return;
    }

    // 1. 업무서약기간 날짜 유효성 검증
    if (pledgePeriod && pledgePeriod[0] && pledgePeriod[1]) {
      if (dayjs(pledgePeriod[0]).isAfter(dayjs(pledgePeriod[1]))) {
        messageApi.warning(
          "업무서약기간 - 시작일이 종료일보다 클 수 없습니다.",
        );
        return;
      }
    }

    // 2. 담당 직무 기간 날짜 유효성 검증
    for (let i = 0; i < tasks.length; i++) {
      const task = tasks[i];
      if (task.period && task.period[0] && task.period[1]) {
        if (dayjs(task.period[0]).isAfter(dayjs(task.period[1]))) {
          messageApi.warning(
            `업무 ${i + 1} - 시작일이 종료일보다 클 수 없습니다.`,
          );
          return;
        }
      }
    }

    // 3. 팀기여 부문 기간 날짜 유효성 검증
    for (const item of teamContribution) {
      if (item.period && item.period[0] && item.period[1]) {
        if (dayjs(item.period[0]).isAfter(dayjs(item.period[1]))) {
          messageApi.warning(
            `팀기여 부문 ${item.category} - 시작일이 종료일보다 클 수 없습니다.`,
          );
          return;
        }
      }
    }

    // 4. 자기개발 부문 기간 날짜 유효성 검증
    for (const item of selfDevelopment) {
      if (item.period && item.period[0] && item.period[1]) {
        if (dayjs(item.period[0]).isAfter(dayjs(item.period[1]))) {
          messageApi.warning(
            `자기개발 부문 ${item.category} - 시작일이 종료일보다 클 수 없습니다.`,
          );
          return;
        }
      }
    }

    // 5. 업무 목표 500자 검증
    for (let i = 0; i < tasks.length; i++) {
      const task = tasks[i];
      if (task.goal.length > 500) {
        messageApi.error(
          `업무 ${i + 1}의 업무 목표는 500자 이하로 입력해주세요. (현재 ${task.goal.length}자)`,
        );
        return;
      }
    }

    // 6. 팀기여 부문 내용 500자 검증
    for (const item of teamContribution) {
      if (item.content.length > 500) {
        messageApi.error(
          `팀기여 부문 ${item.category}의 내용은 500자 이하로 입력해주세요. (현재 ${item.content.length}자)`,
        );
        return;
      }
    }

    // 7. 자기개발 부문 내용 500자 검증
    for (const item of selfDevelopment) {
      if (item.content.length > 500) {
        messageApi.error(
          `자기개발 부문 ${item.category}의 내용은 500자 이하로 입력해주세요. (현재 ${item.content.length}자)`,
        );
        return;
      }
    }

    openConfirmModal({
      title: "저장하시겠습니까?",
      content: "입력한 내용이 임시 저장됩니다.",
      okText: "저장",
      cancelText: "취소",
      onOk: () => {
        performSave();
      },
    });
  };

  /**
   * 기능 : performSave
   * 설명 : 실제 저장 로직 실행 (TEVL010 저장 + TEVL009 저장)
   **/
  const performSave = () => {
    // 날짜 형식 변환 함수 (dayjs -> YYYYMMDD)
    const formatDate = (date: any): string => {
      if (!date) return "";
      return dayjs(date).format("YYYYMMDD");
    };

    // 날짜 범위 분해 (YYYYMMDD-YYYYMMDD -> [YYYYMMDD, YYYYMMDD])
    const splitPeriod = (period: [any, any] | null): [string, string] => {
      if (!period || !period[0] || !period[1]) return ["", ""];
      return [
        dayjs(period[0]).format("YYYYMMDD"),
        dayjs(period[1]).format("YYYYMMDD"),
      ];
    };

    // SNO를 2자리 문자열로 변환
    const formatSno = (num: number): string => {
      return num.toString().padStart(2, "0");
    };

    // DTO 구조에 맞게 요청 데이터 생성
    const requestData: any = {
      empNo: empNo, // props에서 받은 값 사용
      evlYear: evlYear,
      // === TEVL010 (본인정보) - root 레벨 ===
      grdCd: grdCd,
      currPosStDt: formatDate(currentGradeStartDate),
      bizPlgStDt: splitPeriod(pledgePeriod)[0],
      bizPlgEdDt: splitPeriod(pledgePeriod)[1],
      // === TEVL009 (상세) ===
      list: [],
    };

    // 담당 직무 추가 (동적 hrEvlItmId 사용)
    tasks.forEach((task, index) => {
      const [stDt, edDt] = splitPeriod(task.period);
      requestData.list.push({
        hrEvlItmId: task.hrEvlItmId || taskHrEvlItmId || "",
        sno: formatSno(index + 1),
        taskNm: task.name,
        goalCntn: task.goal,
        weightRt: task.weight ? parseInt(task.weight) : 0,
        bizPlgStDt: stDt,
        bizPlgEdDt: edDt,
      });
    });

    // 팀기여 부문 추가 (동적 hrEvlItmId 사용, SNO는 itmSeq 우선)
    teamContribution.forEach((item) => {
      const [stDt, edDt] = splitPeriod(item.period);
      const sno = item.itmSeq
        ? String(item.itmSeq).padStart(2, "0")
        : item.sno || "01";
      requestData.list.push({
        hrEvlItmId: item.hrEvlItmId || item.key || "",
        sno: sno,
        taskNm: item.category,
        goalCntn: item.content,
        weightRt: 0,
        bizPlgStDt: stDt,
        bizPlgEdDt: edDt,
      });
    });

    // 자기개발 부문 추가 (동적 hrEvlItmId 사용, SNO는 itmSeq 우선)
    selfDevelopment.forEach((item) => {
      const [stDt, edDt] = splitPeriod(item.period);
      const sno = item.itmSeq
        ? String(item.itmSeq).padStart(2, "0")
        : item.sno || "01";
      requestData.list.push({
        hrEvlItmId: item.hrEvlItmId || item.key || "",
        sno: sno,
        taskNm: item.category,
        goalCntn: item.content,
        weightRt: 0,
        bizPlgStDt: stDt,
        bizPlgEdDt: edDt,
      });
    });

    // API 호출
    commonProxy.callAsyncSvc(requestData, {
      txCd: "TREVLS0015", // 업무서약서 저장
      success: (responseData: any) => {
        messageApi.success("저장이 완료되었습니다.");
        // 저장 후 재조회
        loadPledgeData();
      },
      error: (errorMsg: string) => {
        messageApi.error(errorMsg || "저장에 실패했습니다.");
      },
    });
  };

  /**
   * 기능 : updateEtcRow
   * 설명 : 기타 서약 부문의 특정 항목을 업데이트
   **/
  const updateEtcRow = (
    type: "team" | "self",
    key: string,
    field: "content" | "period",
    value: any,
  ) => {
    if (type === "team") {
      setTeamContribution((prev) => {
        const updated = prev.map((item) => {
          if (item.key === key && item[field] !== value) {
            return { ...item, [field]: value };
          }
          return item;
        });
        return updated.some((item, idx) => item !== prev[idx]) ? updated : prev;
      });
    } else {
      setSelfDevelopment((prev) => {
        const updated = prev.map((item) => {
          if (item.key === key && item[field] !== value) {
            return { ...item, [field]: value };
          }
          return item;
        });
        return updated.some((item, idx) => item !== prev[idx]) ? updated : prev;
      });
    }
  };

  /**
   * 기능 : totalWeight
   * 설명 : 현재 비중 합계 값을 저장하는 변수 (합계 표시 및 유효성 검사에 사용)
   **/
  const totalWeight = getTotalWeight();

  // 로딩 화면
  if (loading)
    return (
      <div
        style={{
          position: "fixed",
          inset: 0,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          zIndex: 999, // 모달(z-index: 1000)보다 낮게 설정하여 background에만 표시
          background: "rgba(255,255,255,0.6)",
        }}
      >
        <Spin size="large" />
      </div>
    );

  return (
    <>
      {contextHolder}

      <div
        style={{
          padding: isMobile ? "12px" : "20px",
          marginTop: "30px",
          backgroundColor: "#f5f5f5",
          paddingBottom: !isReadonly ? "50px" : "10px",
          width: screenWidth - 20 + "px",
        }}
      >
        {/* 헤더 */}
        <div
          style={{
            textAlign: "center",
            marginBottom: "12px",
          }}
        >
          <Text type="secondary">{evlYear} 년도</Text>
          <div style={{ margin: "5px 0" }}>
            <Text
              strong
              style={{
                fontSize: "22px",
              }}
            >
              업무서약서
            </Text>
          </div>
        </div>

        {/* 본인 정보 */}
        <Card
          title={
            <span style={{ fontSize: "16px", fontWeight: 600 }}>본인 정보</span>
          }
          style={{
            marginBottom: "12px",
            borderRadius: "12px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
          }}
          bodyStyle={{ padding: "20px" }}
        >
          {/* 이름, 직군 - 2컬럼 그리드 */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(2, 1fr)",
              gap: "16px",
              marginBottom: "16px",
            }}
          >
            <div>
              <div
                style={{
                  fontSize: "12px",
                  color: "#999",
                  marginBottom: "6px",
                  fontWeight: 500,
                }}
              >
                이름
              </div>
              <div
                style={{
                  padding: "6px 10px",
                  backgroundColor: "#f5f5f5",
                  color: "#8c8c8c",
                  fontWeight: 500,
                  fontSize: "11px",
                  border: "1px solid #e8e8e8",
                  borderRadius: "6px",
                }}
              >
                {userInfo.userNm}
              </div>
            </div>
            <div>
              <div
                ref={grdSelectRef}
                style={{
                  fontSize: "12px",
                  color: "#999",
                  marginBottom: "6px",
                  fontWeight: 500,
                }}
              >
                직군
              </div>
              <Select
                value={grdCd || undefined}
                onChange={(value) => {
                  setGrdCd(value);
                  const selected = grdCdOptions.find((opt) => opt.cd === value);
                  setUserInfo((prev) => ({
                    ...prev,
                    grdNm: selected?.cdNm || "",
                  }));
                }}
                placeholder="선택"
                style={{ width: "100%" }}
                disabled={!isEditable}
              >
                {grdCdOptions.map((opt) => (
                  <Select.Option key={opt.cd} value={opt.cd}>
                    {opt.cdNm}
                  </Select.Option>
                ))}
              </Select>
            </div>
          </div>

          {/* 직위, 직급 - 2컬럼 그리드 */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(2, 1fr)",
              gap: "16px",
              marginBottom: "16px",
            }}
          >
            <div>
              <div
                style={{
                  fontSize: "12px",
                  color: "#999",
                  marginBottom: "6px",
                  fontWeight: 500,
                }}
              >
                직급
              </div>
              <div
                style={{
                  padding: "6px 10px",
                  backgroundColor: "#f5f5f5",
                  color: "#8c8c8c",
                  fontWeight: 500,
                  fontSize: "11px",
                  border: "1px solid #e8e8e8",
                  borderRadius: "6px",
                }}
              >
                {userInfo.postNm}
              </div>
            </div>
            <div>
              <div
                style={{
                  fontSize: "12px",
                  color: "#999",
                  marginBottom: "6px",
                  fontWeight: 500,
                }}
              >
                직위
              </div>
              <div
                style={{
                  padding: "6px 10px",
                  backgroundColor: "#f5f5f5",
                  color: "#8c8c8c",
                  fontWeight: 500,
                  fontSize: "11px",
                  border: "1px solid #e8e8e8",
                  borderRadius: "6px",
                }}
              >
                {userInfo.dutyNm}
              </div>
            </div>
          </div>

          {/* 사업부, 소속팀 - 2컬럼 그리드 */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(2, 1fr)",
              gap: "16px",
              marginBottom: "16px",
            }}
          >
            <div>
              <div
                style={{
                  fontSize: "12px",
                  color: "#999",
                  marginBottom: "6px",
                  fontWeight: 500,
                }}
              >
                사업부
              </div>
              <div
                style={{
                  padding: "6px 10px",
                  backgroundColor: "#f5f5f5",
                  color: "#8c8c8c",
                  fontWeight: 500,
                  fontSize: "11px",
                  border: "1px solid #e8e8e8",
                  borderRadius: "6px",
                }}
              >
                {userInfo.upperDeptNm}
              </div>
            </div>
            <div>
              <div
                style={{
                  fontSize: "12px",
                  color: "#999",
                  marginBottom: "6px",
                  fontWeight: 500,
                }}
              >
                소속팀
              </div>
              <div
                style={{
                  padding: "6px 10px",
                  backgroundColor: "#f5f5f5",
                  color: "#8c8c8c",
                  fontWeight: 500,
                  fontSize: "11px",
                  border: "1px solid #e8e8e8",
                  borderRadius: "6px",
                }}
              >
                {userInfo.deptNm}
              </div>
            </div>
          </div>

          {/* 현직급시작일자 - 단일 컬럼 */}
          <div ref={currentGradeStartDateRef} style={{ marginBottom: "16px" }}>
            <div
              style={{
                fontSize: "12px",
                color: "#999",
                marginBottom: "6px",
                fontWeight: 500,
              }}
            >
              현직급시작일자
            </div>
            <DatePicker
              value={currentGradeStartDate}
              onChange={(date) => {
                // 오늘 이후의 날짜 선택 시 경고
                if (date && date.isAfter(dayjs())) {
                  messageApi.error(
                    "현직급시작일자는 과거일자 또는 오늘일자만 선택가능합니다.",
                  );
                  setCurrentGradeStartDate(null);
                  return;
                }
                setCurrentGradeStartDate(date);
              }}
              style={{ width: "100%" }}
              disabled={!isEditable}
            />
          </div>

          {/* 업무서약기간 - 단일 컬럼 */}
          <div>
            <div
              style={{
                fontSize: "12px",
                color: "#999",
                marginBottom: "6px",
                fontWeight: 500,
              }}
            >
              업무서약기간
            </div>
            <div style={{ display: "flex", gap: "8px" }}>
              <DatePicker
                placeholder="시작일"
                value={pledgePeriod[0]}
                onChange={(date) => setPledgePeriod([date, pledgePeriod[1]])}
                style={{ flex: 1 }}
                disabled={!isEditable}
              />
              <DatePicker
                placeholder="종료일"
                value={pledgePeriod[1]}
                onChange={(date) => setPledgePeriod([pledgePeriod[0], date])}
                style={{ flex: 1 }}
                disabled={!isEditable}
              />
            </div>
          </div>
        </Card>

        {/* 담당 직무 */}
        <div ref={tasksContainerRef}>
          <Card
            title={
              <span style={{ fontSize: "16px", fontWeight: 600 }}>
                담당 직무 부문
              </span>
            }
            style={{
              marginBottom: "12px",
              borderRadius: "12px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
            }}
            bodyStyle={{ padding: "16px" }}
          >
            <div
              style={{
                marginBottom: "12px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "8px",
              }}
            >
              <Text strong style={{ fontSize: "14px", color: "#333" }}>
                담당 직무
              </Text>
              {isEditable && (
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={addTask}
                >
                  업무 추가
                </Button>
              )}
            </div>

            <div
              style={{
                marginBottom: "16px",
                padding: "12px",
                backgroundColor: "#f5f5f5",
                borderRadius: "8px",
                fontSize: "12px",
                color: "#666",
              }}
            >
              업무 비중의 합계는 100%여야 제출 가능합니다.
            </div>

            <Space direction="vertical" size="small" style={{ width: "100%" }}>
              {tasks.map((task, index) => (
                <Card
                  key={task.id}
                  data-task-index={index}
                  size="small"
                  style={{
                    borderRadius: "8px",
                    boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
                  }}
                  bodyStyle={{ padding: "12px" }}
                >
                  <div
                    style={{
                      marginBottom: "12px",
                      display: "flex",
                      justifyContent: "space-between",
                    }}
                  >
                    <Text strong style={{ fontSize: "14px", color: "#333" }}>
                      업무 {index + 1}
                    </Text>
                    {tasks.length > 1 && isEditable && (
                      <Popconfirm
                        title="정말로 삭제하시겠습니까?"
                        onConfirm={() => removeTask(task.id)}
                        okText="네"
                        cancelText="아니오"
                        style={{ float: "right" }}
                      >
                        <Button
                          danger
                          size="small"
                          style={{ marginLeft: "5px" }}
                        >
                          삭제
                        </Button>
                      </Popconfirm>
                    )}
                  </div>

                  <Space
                    direction="vertical"
                    size="small"
                    style={{ width: "100%" }}
                  >
                    <div>
                      <div
                        style={{
                          fontSize: "12px",
                          color: "#666",
                          marginBottom: "4px",
                          fontWeight: 500,
                        }}
                      >
                        업무명
                      </div>
                      <Input
                        key={`name-${task.id}-${task.name || ""}`}
                        defaultValue={task.name}
                        maxLength={100}
                        onBlur={(e) =>
                          updateTask(task.id, "name", e.target.value)
                        }
                        placeholder="업무명을 입력하세요"
                        disabled={!isEditable}
                      />
                    </div>

                    <div>
                      <div
                        style={{
                          fontSize: "12px",
                          color: "#666",
                          marginBottom: "4px",
                          fontWeight: 500,
                        }}
                      >
                        비중 (%)
                      </div>
                      <Input
                        value={task.weight}
                        onChange={(e) => {
                          const val = e.target.value;
                          // 빈 문자열이거나 1-100 범위의 정수만 허용
                          if (val === "") {
                            updateTask(task.id, "weight", "");
                          } else if (/^[0-9]+$/.test(val)) {
                            const num = parseInt(val, 10);
                            if (num >= 1 && num <= 100) {
                              updateTask(task.id, "weight", val);
                            }
                          }
                        }}
                        placeholder="비중을 입력하세요 (1-100)"
                        disabled={!isEditable}
                      />
                    </div>

                    <div>
                      <div
                        style={{
                          fontSize: "12px",
                          color: "#666",
                          marginBottom: "4px",
                          fontWeight: 500,
                        }}
                      >
                        기간
                      </div>
                      <div style={{ display: "flex", gap: "8px" }}>
                        <DatePicker
                          placeholder="시작일"
                          value={task.period ? task.period[0] : null}
                          onChange={(date) => {
                            setTasks((prev) =>
                              prev.map((t) =>
                                t.id === task.id
                                  ? {
                                      ...t,
                                      period: [
                                        date,
                                        t.period ? t.period[1] : null,
                                      ],
                                    }
                                  : t,
                              ),
                            );
                          }}
                          style={{ flex: 1 }}
                          disabled={!isEditable}
                        />
                        <DatePicker
                          placeholder="종료일"
                          value={task.period ? task.period[1] : null}
                          onChange={(date) => {
                            setTasks((prev) =>
                              prev.map((t) =>
                                t.id === task.id
                                  ? {
                                      ...t,
                                      period: [
                                        t.period ? t.period[0] : null,
                                        date,
                                      ],
                                    }
                                  : t,
                              ),
                            );
                          }}
                          style={{ flex: 1 }}
                          disabled={!isEditable}
                        />
                      </div>
                    </div>

                    <div style={{ marginBottom: 15 }}>
                      <div
                        style={{
                          fontSize: "12px",
                          color: "#666",
                          marginBottom: "4px",
                          fontWeight: 500,
                        }}
                      >
                        업무 목표
                      </div>
                      <TextArea
                        rows={4}
                        value={task.goal}
                        maxLength={500}
                        showCount
                        onChange={(e) =>
                          updateTask(task.id, "goal", e.target.value)
                        }
                        placeholder="업무 목표를 입력하세요"
                        disabled={!isEditable}
                      />
                    </div>
                  </Space>
                </Card>
              ))}
            </Space>

            <div
              style={{
                marginTop: "16px",
                padding: "12px",
                backgroundColor: totalWeight !== 100 ? "#fff2f0" : "#f6ffed",
                border: `1px solid ${totalWeight !== 100 ? "#ffccc7" : "#b7eb8f"}`,
                borderRadius: "8px",
                textAlign: "center",
              }}
            >
              <Text
                strong
                style={{ color: totalWeight !== 100 ? "#cf1322" : "#52c41a" }}
              >
                합계: {totalWeight}%{" "}
                {totalWeight !== 100 && "(100%가 되어야 제출 가능)"}
              </Text>
            </div>
          </Card>
        </div>

        {/* 기타 서약 부문 */}
        <Card
          title={
            <span style={{ fontSize: "16px", fontWeight: 600 }}>
              기타 서약 부문
            </span>
          }
          style={{
            marginBottom: "12px",
            borderRadius: "12px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
          }}
          bodyStyle={{ padding: "16px" }}
        >
          {/* 팀기여 부문 */}
          <div style={{ marginBottom: "24px" }}>
            <div style={{ marginBottom: "16px" }}>
              <Text strong style={{ fontSize: "14px", color: "#333" }}>
                팀 기여 부문
              </Text>
            </div>

            <Space direction="vertical" size="small" style={{ width: "100%" }}>
              {teamContribution.map((item, index) => (
                <Card
                  key={item.key}
                  data-team-index={index}
                  size="small"
                  style={{
                    borderRadius: "8px",
                    boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
                  }}
                  bodyStyle={{ padding: "12px" }}
                >
                  <div style={{ marginBottom: "12px" }}>
                    <Text strong style={{ fontSize: "14px", color: "#333" }}>
                      {item.category}
                    </Text>
                  </div>

                  <Space
                    direction="vertical"
                    size="small"
                    style={{ width: "100%" }}
                  >
                    <div style={{ marginBottom: 12 }}>
                      <div
                        style={{
                          fontSize: "12px",
                          color: "#666",
                          marginBottom: "4px",
                          fontWeight: 500,
                        }}
                      >
                        내용
                      </div>
                      <TextArea
                        rows={3}
                        value={item.content}
                        maxLength={500}
                        showCount
                        onChange={(e) =>
                          updateEtcRow(
                            "team",
                            item.key,
                            "content",
                            e.target.value,
                          )
                        }
                        placeholder="내용을 입력하세요"
                        disabled={!isEditable}
                      />
                    </div>

                    <div>
                      <div
                        style={{
                          fontSize: "12px",
                          color: "#666",
                          marginBottom: "4px",
                          fontWeight: 500,
                        }}
                      >
                        기간
                      </div>
                      <div style={{ display: "flex", gap: "8px" }}>
                        <DatePicker
                          placeholder="시작일"
                          value={item.period ? item.period[0] : null}
                          onChange={(date) => {
                            const newPeriod = [
                              date,
                              item.period ? item.period[1] : null,
                            ];
                            updateEtcRow("team", item.key, "period", newPeriod);
                          }}
                          style={{ flex: 1 }}
                          disabled={!isEditable}
                        />
                        <DatePicker
                          placeholder="종료일"
                          value={item.period ? item.period[1] : null}
                          onChange={(date) => {
                            const newPeriod = [
                              item.period ? item.period[0] : null,
                              date,
                            ];
                            updateEtcRow("team", item.key, "period", newPeriod);
                          }}
                          style={{ flex: 1 }}
                          disabled={!isEditable}
                        />
                      </div>
                    </div>
                  </Space>
                </Card>
              ))}
            </Space>
          </div>

          {/* 자기개발 부문 */}
          <div>
            <div style={{ marginBottom: "16px" }}>
              <Text strong style={{ fontSize: "14px", color: "#333" }}>
                자기개발 부문
              </Text>
            </div>

            <Space direction="vertical" size="small" style={{ width: "100%" }}>
              {selfDevelopment.map((item, index) => (
                <Card
                  key={item.key}
                  data-self-index={index}
                  size="small"
                  style={{
                    borderRadius: "8px",
                    boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
                  }}
                  bodyStyle={{ padding: "12px" }}
                >
                  <div style={{ marginBottom: "12px" }}>
                    <Text strong style={{ fontSize: "14px", color: "#333" }}>
                      {item.category}
                    </Text>
                  </div>

                  <Space
                    direction="vertical"
                    size="small"
                    style={{ width: "100%" }}
                  >
                    <div style={{ marginBottom: 12 }}>
                      <div
                        style={{
                          fontSize: "12px",
                          color: "#666",
                          marginBottom: "4px",
                          fontWeight: 500,
                        }}
                      >
                        내용
                      </div>
                      <TextArea
                        rows={3}
                        value={item.content}
                        maxLength={500}
                        showCount
                        onChange={(e) =>
                          updateEtcRow(
                            "self",
                            item.key,
                            "content",
                            e.target.value,
                          )
                        }
                        placeholder="내용을 입력하세요"
                        disabled={!isEditable}
                      />
                    </div>

                    <div>
                      <div
                        style={{
                          fontSize: "12px",
                          color: "#666",
                          marginBottom: "4px",
                          fontWeight: 500,
                        }}
                      >
                        기간
                      </div>
                      <div style={{ display: "flex", gap: "8px" }}>
                        <DatePicker
                          placeholder="시작일"
                          value={item.period ? item.period[0] : null}
                          onChange={(date) => {
                            const newPeriod = [
                              date,
                              item.period ? item.period[1] : null,
                            ];
                            updateEtcRow("self", item.key, "period", newPeriod);
                          }}
                          style={{ flex: 1 }}
                          disabled={!isEditable}
                        />
                        <DatePicker
                          placeholder="종료일"
                          value={item.period ? item.period[1] : null}
                          onChange={(date) => {
                            const newPeriod = [
                              item.period ? item.period[0] : null,
                              date,
                            ];
                            updateEtcRow("self", item.key, "period", newPeriod);
                          }}
                          style={{ flex: 1 }}
                          disabled={!isEditable}
                        />
                      </div>
                    </div>
                  </Space>
                </Card>
              ))}
            </Space>
          </div>
        </Card>
      </div>

      {/* 저장 및 제출 버튼 - 고정 배치 */}
      {!isReadonly && (
        <div
          style={{
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            padding: "12px",
            backgroundColor: "#fff",
            borderTop: "1px solid #f0f0f0",
            display: "flex",
            gap: "8px",
            zIndex: 1000,
            justifyContent:
              bizPlgSubYn === "Y" && isWithinDeadline ? "center" : "initial",
          }}
        >
          {!(bizPlgSubYn === "Y" && isWithinDeadline) && (
            <>
              <Button
                size="large"
                icon={<SaveOutlined />}
                onClick={handleSave}
                disabled={!isEditable}
                style={{
                  flex: 1,
                  height: "48px",
                  fontSize: "16px",
                  fontWeight: 600,
                }}
              >
                저장
              </Button>
              {bizPlgSubYn !== "Y" && evlEnableYn === "Y" && (
                <Button
                  type="primary"
                  size="large"
                  icon={<SendOutlined />}
                  onClick={handleSubmit}
                  disabled={!isEditable}
                  style={{
                    flex: 1,
                    height: "48px",
                    fontSize: "16px",
                    fontWeight: 600,
                  }}
                >
                  제출
                </Button>
              )}
            </>
          )}
          {bizPlgSubYn === "Y" && isWithinDeadline && (
            <Button
              danger
              size="large"
              onClick={handleCancelSubmit}
              style={{
                flex: 1,
                height: "48px",
                fontSize: "16px",
                fontWeight: 600,
              }}
            >
              제출취소
            </Button>
          )}
          {bizPlgSubYn === "Y" && !isWithinDeadline && (
            <Button
              size="large"
              icon={<CheckCircleFilled />}
              disabled
              style={{
                flex: 1,
                height: "48px",
                fontSize: "16px",
                fontWeight: 600,
              }}
            >
              제출완료
            </Button>
          )}
        </div>
      )}
    </>
  );
};

export default Evl0102m;
